// Per-visitor limited-time offer cycle.
//
// On a visitor's first visit we pick a random deadline 24-72 hours out and
// persist it in localStorage, so the countdown is consistent across pages and
// reloads. When the deadline expires, the offer goes quiet for a random 1-3
// days (no banner shown), after which a fresh random cycle starts.

const DEADLINE_KEY = "offerCountdownDeadline"
const QUIET_UNTIL_KEY = "offerCountdownQuietUntil"

const HOUR_MS = 60 * 60 * 1000
const MIN_OFFER_MS = 24 * HOUR_MS
const MAX_OFFER_MS = 72 * HOUR_MS
const MIN_QUIET_MS = 24 * HOUR_MS
const MAX_QUIET_MS = 72 * HOUR_MS

// Fallback when localStorage is unavailable (private mode, blocked storage).
const memoryStore = {}

function readNumber(key) {
  let raw
  try {
    raw = window.localStorage.getItem(key)
  } catch {
    raw = memoryStore[key]
  }
  const value = parseInt(raw, 10)
  return Number.isFinite(value) ? value : null
}

function writeNumber(key, value) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    memoryStore[key] = String(value)
  }
}

function removeKey(key) {
  try {
    window.localStorage.removeItem(key)
  } catch {
    delete memoryStore[key]
  }
}

function randomDurationBetween(minMs, maxMs) {
  return minMs + Math.floor(Math.random() * (maxMs - minMs))
}

function startNewCycle(now) {
  const deadline = now + randomDurationBetween(MIN_OFFER_MS, MAX_OFFER_MS)
  writeNumber(DEADLINE_KEY, deadline)
  removeKey(QUIET_UNTIL_KEY)
  return deadline
}

// Returns { active: true, deadline } while an offer is running, or
// { active: false } during the quiet period between cycles.
export function currentOfferState(now = Date.now()) {
  const quietUntil = readNumber(QUIET_UNTIL_KEY)
  if (quietUntil) {
    if (now < quietUntil) return { active: false }
    return { active: true, deadline: startNewCycle(now) }
  }

  const deadline = readNumber(DEADLINE_KEY)
  if (!deadline) return { active: true, deadline: startNewCycle(now) }
  if (now < deadline) return { active: true, deadline }

  // Offer just expired: start a quiet period anchored to the expiry time.
  const quietEnd = deadline + randomDurationBetween(MIN_QUIET_MS, MAX_QUIET_MS)
  removeKey(DEADLINE_KEY)
  if (now < quietEnd) {
    writeNumber(QUIET_UNTIL_KEY, quietEnd)
    return { active: false }
  }

  // Visitor returned long after expiry; the quiet period has already lapsed.
  return { active: true, deadline: startNewCycle(now) }
}

export function formatRemaining(deadline, now = Date.now()) {
  const distance = Math.max(deadline - now, 0)
  const days = Math.floor(distance / (24 * HOUR_MS))
  const hours = Math.floor((distance % (24 * HOUR_MS)) / HOUR_MS)
  const minutes = Math.floor((distance % HOUR_MS) / (60 * 1000))
  const seconds = Math.floor((distance % (60 * 1000)) / 1000)
  return `${days}d ${hours}h ${minutes}m ${seconds}s`
}
